import { FormEvent, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { firebaseAuth } from '~/config/firebase.config';
import { STRIPE_SERVER } from '~/config/global.config';

type StripeServerFormProps = {
    route: '/create-customer-portal-session' | '/reactivate-subscription';
    clientId: string;
    lang: string;
    /**
     * Ouvre directement l'écran de confirmation du changement de formule :
     * `annual` (« Passer à l'annuel »), `pro` (« Passer en Pro », Ma marque), `pro_annual` (Pro à l'année).
     */
    intent?: 'annual' | 'pro' | 'pro_annual';
    className?: string;
    children: ReactNode;
};

/**
 * Formulaire POST vers stripe-b2b (portail client, réactivation) : navigation
 * pleine page vers Stripe, donc pas d'en-tête Authorization possible. Le jeton
 * Firebase part dans un champ caché `idToken`, ajouté au moment de l'envoi
 * (jeton frais) ; le serveur vérifie que l'utilisateur est admin de
 * l'établissement et lit lui-même le client Stripe dans Firestore. Sans jeton
 * (session expirée, réseau), le serveur refuserait : on n'envoie pas, on
 * demande de recharger la page.
 */
export function StripeServerForm({ route, clientId, lang, intent, className, children }: StripeServerFormProps) {
    const { t } = useTranslation();
    const tokenRef = useRef<HTMLInputElement>(null);
    const [noToken, setNoToken] = useState(false);

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        let token: string | undefined;
        try {
            token = await firebaseAuth.currentUser?.getIdToken();
        } catch {
            token = undefined;
        }
        if (!token || !tokenRef.current) {
            setNoToken(true);
            return;
        }
        setNoToken(false);
        tokenRef.current.value = token;
        // `submit()` ne redéclenche pas onSubmit.
        form.submit();
    };

    return (
        <form className={className} action={`${STRIPE_SERVER}${route}`} method="POST" onSubmit={onSubmit}>
            <input type="hidden" name="idToken" ref={tokenRef} defaultValue="" />
            <input type="hidden" name="clientId" value={clientId} />
            <input type="hidden" name="lang" value={lang.split('-')[0]} />
            {intent && <input type="hidden" name="intent" value={intent} />}
            {children}
            {noToken && (
                <p role="alert" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--pink, #ff2453)' }}>
                    {t('account.sessionExpiredReload')}
                </p>
            )}
        </form>
    );
}
